import { useEffect, useState } from "react";
import {
  builtinFieldPresets,
  effectiveFieldPresets,
  applyFieldPreset,
  validationRules,
  validationSettingsSchema,
  formatSettingsSchema,
  safePattern,
  newField,
  formatValue,
  policyValueError,
  strictDate,
  strictDateTime,
  strictTime,
  supportedDateLocales,
  type Field,
  type Project,
  type FieldPreset,
  type FieldPresetCatalog,
  type FieldPresetScope,
  type FieldPresetWrite,
} from "@proof/core";
import { useAuthoringServices } from "./services";
import { Button, Control, Modal, Toggle } from "./components";
type Kind = "validation" | "format";
type Settings = Field["validation"] | Field["format"];
function parse(kind: Kind, value: Settings) {
  const result = (
    kind === "validation" ? validationSettingsSchema : formatSettingsSchema
  ).parse(value);
  if (kind === "validation" && "pattern" in result && result.pattern)
    safePattern(result.pattern);
  return result;
}
/** A local draft keeps partial/invalid patterns out of the document until Apply. */
export function PolicySettingsEditor({
  kind,
  value,
  onApply,
  onPendingChange,
  fieldType,
}: {
  kind: Kind;
  fieldType?: Field["type"];
  value: Settings;
  onApply(value: Settings): void;
  onPendingChange?(pending: boolean): void;
}) {
  const [valueType, setValueType] = useState<Field["type"]>(
    fieldType || "text",
  );
  const numeric = (fieldType || valueType) === "number";
  const [draft, setDraft] = useState<Record<string, any>>(value),
    [sample, setSample] = useState(
      kind === "format" ? "1234.567" : "https://example.com",
    );
  useEffect(() => {
    setDraft(value);
  }, [value]);
  useEffect(() => {
    onPendingChange?.(JSON.stringify(draft) !== JSON.stringify(value));
  }, [draft, value, onPendingChange]);
  const set = (key: string, value: unknown) =>
    setDraft({ ...draft, [key]: value, preset: undefined });
  const applicable = (value: Record<string, any>) => {
    if (kind !== "validation") return value;
    const result = { ...value };
    if (numeric) delete result.maxLength;
    else {
      delete result.min;
      delete result.max;
    }
    return result;
  };
  let error = "";
  try {
    parse(kind, applicable(draft));
  } catch (e) {
    error =
      "issues" in (e as object)
        ? (e as { issues: { message: string }[] }).issues
            .map((issue) => issue.message)
            .join("; ")
        : (e as Error).message;
  }
  let preview = "";
  if (!error) {
    const f = { ...newField(fieldType || valueType), [kind]: parse(kind, applicable(draft)) };
    preview =
      kind === "format"
        ? formatValue(f, sample)
        : policyValueError(f, sample) || "Valid value";
    if (
      kind === "format" &&
      draft.kind === "date" &&
      !strictDate(sample) &&
      !strictDateTime(sample) &&
      !strictTime(sample)
    )
      preview = "Enter an ISO date, date/time, or time to preview.";
  }
  return (
    <div className="policy-settings-editor">
      {!fieldType && (
        <Control label="Input value type">
          <select
            value={valueType}
            onChange={(event) =>
              setValueType(event.target.value as Field["type"])
            }
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
          </select>
        </Control>
      )}
      {kind === "validation" ? (
        <>
          <Control label="Validation rule">
            <select
              value={draft.rule || "auto"}
              onChange={(e) => set("rule", e.target.value)}
            >
              {validationRules.map((rule) => (
                <option key={rule} value={rule}>
                  {(
                    {
                      auto: "Automatic (including website URLs)",
                      none: "No additional rule",
                      url: "Website URL (HTTP / HTTPS)",
                      "postal-us": "US ZIP / ZIP+4",
                      "postal-ca": "Canadian postal code",
                      "hex-color": "Hex color",
                      nonnegative: "Zero or positive number",
                    } as Record<string, string>
                  )[rule] || rule}
                </option>
              ))}
            </select>
          </Control>
          <div className="control-grid">
            {(numeric ? ["min", "max"] : ["maxLength"]).map((key) => (
              <Control
                key={key}
                label={
                  key === "maxLength"
                    ? "Maximum characters"
                    : key === "min"
                      ? "Minimum value"
                      : "Maximum value"
                }
              >
                <input
                  type="number"
                  value={draft[key] ?? ""}
                  onChange={(e) =>
                    set(
                      key,
                      e.target.value === ""
                        ? undefined
                        : Number(e.target.value),
                    )
                  }
                />
              </Control>
            ))}
          </div>
          <Control
            label="Validation expression"
            note="Optional Unicode regular expression. Unsafe patterns are rejected."
          >
            <input
              value={draft.pattern || ""}
              onChange={(e) => set("pattern", e.target.value || undefined)}
            />
          </Control>
        </>
      ) : (
        <>
          <Control label="Output formatting">
            <select
              value={draft.kind || "raw"}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  preset: undefined,
                  kind: e.target.value,
                  ...(e.target.value === "date"
                    ? {
                        datePattern: draft.datePattern || "yyyy-MM-dd",
                        locale: supportedDateLocales.includes(draft.locale)
                          ? draft.locale
                          : "en-US",
                      }
                    : {}),
                  ...(e.target.value === "number"
                    ? {
                        numberPattern: draft.numberPattern || "#,##0.00",
                        currency: draft.currency || "USD",
                      }
                    : {}),
                })
              }
            >
              <option value="raw">Original value</option>
              <option value="text">Text</option>
              <option value="date">Date / time</option>
              <option value="number">Number</option>
            </select>
          </Control>
          {draft.kind === "date" && (
            <>
              <Control label="Date / time style">
                <select
                  value={
                    [
                      "yyyy-MM-dd",
                      "MM/dd/yyyy",
                      "dd/MM/yyyy",
                      "MMM d, yyyy",
                      "MMMM d, yyyy",
                      "yyyy-MM-dd HH:mm",
                      "HH:mm",
                      "HH:mm:ss",
                      "h:mm a",
                    ].includes(draft.datePattern)
                      ? draft.datePattern
                      : "custom"
                  }
                  onChange={(e) =>
                    e.target.value !== "custom"
                      ? set("datePattern", e.target.value)
                      : set("datePattern", "")
                  }
                >
                  <option value="custom">Custom pattern</option>
                  {[
                    "yyyy-MM-dd",
                    "MM/dd/yyyy",
                    "dd/MM/yyyy",
                    "MMM d, yyyy",
                    "MMMM d, yyyy",
                    "yyyy-MM-dd HH:mm",
                    "HH:mm",
                    "HH:mm:ss",
                    "h:mm a",
                  ].map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
              </Control>
              <Control
                label="Date / time pattern"
                note="Unicode tokens: yyyy year, MM month, dd day, HH hours, mm minutes. Quote literal text. Values use UTC."
              >
                <input
                  value={draft.datePattern || ""}
                  onChange={(e) => set("datePattern", e.target.value)}
                />
              </Control>
            </>
          )}
          {draft.kind === "number" && (
            <>
              <Control label="Number style">
                <select
                  value={
                    [
                      "#,##0",
                      "#,##0.00",
                      "#,##0.####",
                      "0.0%",
                      "000000",
                      "¤#,##0.00",
                    ].includes(draft.numberPattern)
                      ? draft.numberPattern
                      : "custom"
                  }
                  onChange={(e) =>
                    set(
                      "numberPattern",
                      e.target.value === "custom" ? "" : e.target.value,
                    )
                  }
                >
                  <option value="custom">Custom pattern</option>
                  {[
                    "#,##0",
                    "#,##0.00",
                    "#,##0.####",
                    "0.0%",
                    "000000",
                    "¤#,##0.00",
                  ].map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
              </Control>
              <Control
                label="Number pattern"
                note="0 required digit; # optional digit; comma groups; % percent; ¤ currency."
              >
                <input
                  value={draft.numberPattern || ""}
                  onChange={(e) => set("numberPattern", e.target.value)}
                />
              </Control>
              {draft.numberPattern?.includes("¤") && (
                <Control label="Currency code">
                  <input
                    maxLength={3}
                    value={draft.currency || "USD"}
                    onChange={(e) =>
                      set("currency", e.target.value.toUpperCase())
                    }
                  />
                </Control>
              )}
            </>
          )}
          <Control label="Formatting locale">
            {draft.kind === "date" ? (
              <select
                value={draft.locale || "en-US"}
                onChange={(e) => set("locale", e.target.value)}
              >
                {supportedDateLocales.map((l) => (
                  <option key={l}>{l}</option>
                ))}
              </select>
            ) : (
              <input
                value={draft.locale || ""}
                onChange={(e) => set("locale", e.target.value)}
              />
            )}
          </Control>
          <div className="control-grid">
            {["prefix", "suffix"].map((k) => (
              <Control key={k} label={k === "prefix" ? "Prefix" : "Suffix"}>
                <input
                  value={draft[k] || ""}
                  onChange={(e) => set(k, e.target.value)}
                />
              </Control>
            ))}
          </div>
          <Control label="Text case">
            <select
              value={draft.uppercase ? "upper" : draft.casing || "unchanged"}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  preset: undefined,
                  uppercase: e.target.value === "upper",
                  casing:
                    e.target.value === "upper" ? "unchanged" : e.target.value,
                })
              }
            >
              <option value="unchanged">Keep case</option>
              <option value="upper">UPPERCASE</option>
              <option value="lowercase">lowercase</option>
              <option value="title">Title Case</option>
            </select>
          </Control>
          <Toggle
            label="Trim spaces"
            value={!!draft.trim}
            onChange={(v) => set("trim", v)}
          />
        </>
      )}
      <Control label="Test value">
        <input value={sample} onChange={(e) => setSample(e.target.value)} />
      </Control>
      {error ? (
        <p role="alert">{error}</p>
      ) : (
        <p aria-label="Policy preview">{preview}</p>
      )}
      <Button
        disabled={!!error}
        onClick={() => onApply(parse(kind, applicable(draft)))}
      >
        Apply {kind === "format" ? "formatting" : "validation"} settings
      </Button>
    </div>
  );
}
function useCatalog(projectId?: string, fieldId?: string, revision = 0) {
  const services = useAuthoringServices(),
    [catalog, setCatalog] = useState<FieldPresetCatalog>({
      context: {
        siteId: "",
        companyId: "",
        canManageSite: false,
        canManageCompany: false,
        canManageProject: false,
      },
      presets: builtinFieldPresets(),
    }),
    [error, setError] = useState(""),
    [reload, setReload] = useState(0);
  useEffect(() => {
    let active = true;
    setError("");
    setCatalog({
      context: {
        siteId: "",
        companyId: "",
        canManageSite: false,
        canManageCompany: false,
        canManageProject: false,
      },
      presets: builtinFieldPresets(),
    });
    if (!services.listFieldPresets) {
      setCatalog({
        context: {
          siteId: "",
          companyId: "",
          canManageSite: false,
          canManageCompany: false,
          canManageProject: false,
        },
        presets: builtinFieldPresets(),
      });
      return;
    }
    services
      .listFieldPresets({
        ...(projectId ? { projectId } : {}),
        ...(fieldId ? { fieldId } : {}),
      })
      .then((c) => {
        if (active) setCatalog(c);
      })
      .catch((e) => {
        if (active) {
          setError(e.message);
          setCatalog((c) => ({ ...c, presets: builtinFieldPresets() }));
        }
      });
    return () => {
      active = false;
    };
  }, [services.listFieldPresets, projectId, fieldId, reload, revision]);
  return { catalog, error, refresh: () => setReload((n) => n + 1) };
}
export function FieldPolicyControls({
  project,
  field,
  onChange,
  sharedOnly = false,
}: {
  project: Project;
  field: Field;
  onChange(patch: Partial<Field>): void;
  sharedOnly?: boolean;
}) {
  const { editorPresentation } = useAuthoringServices();
  const [revision, setRevision] = useState(0);
  const { catalog, error } = useCatalog(
      sharedOnly ? undefined : project.id,
      sharedOnly ? undefined : field.id,
      revision,
    ),
    [saving, setSaving] = useState<Kind>();
  return (
    <>
      {error && (
        <p role="alert">
          Shared presets unavailable: {error}. Standard presets remain
          available.
        </p>
      )}
      {(["validation", "format"] as const)
        .filter((kind) =>
          kind === "validation"
            ? editorPresentation?.enableValidation !== false
            : editorPresentation?.enableFormatting !== false,
        )
        .map((kind) => {
          const selected = field[kind].preset,
            rows = effectiveFieldPresets(catalog.presets).filter(
              (p) => p.kind === kind,
            );
          return (
            <section
              key={kind}
              aria-label={
                kind === "format" ? "Formatting policy" : "Validation policy"
              }
            >
              <Control
                label={
                  kind === "format" ? "Formatting preset" : "Validation preset"
                }
              >
                <select
                  value={
                    selected ? `${selected.id}:${selected.version}` : "custom"
                  }
                  onChange={(e) => {
                    const preset = rows.find(
                      (p) => `${p.id}:${p.version}` === e.target.value,
                    );
                    if (preset)
                      onChange({
                        [kind]: applyFieldPreset(field, preset)[kind],
                      });
                  }}
                >
                  <option value="custom">Custom variable settings</option>
                  {selected &&
                    !rows.some(
                      (p) =>
                        p.id === selected.id && p.version === selected.version,
                    ) && (
                      <option value={`${selected.id}:${selected.version}`}>
                        {selected.name} · pinned v{selected.version}
                      </option>
                    )}
                  {rows.map((p) => (
                    <option key={p.id} value={`${p.id}:${p.version}`}>
                      {p.name} · {p.scope.kind}
                    </option>
                  ))}
                </select>
              </Control>
              <PolicySettingsEditor
                key={`${field.id}:${kind}`}
                kind={kind}
                fieldType={field.type}
                value={field[kind]}
                onApply={(value) => onChange({ [kind]: value })}
              />
              <Button onClick={() => setSaving(kind)}>
                Save {kind === "format" ? "formatting" : "validation"} as
                reusable preset
              </Button>
            </section>
          );
        })}
      {saving && (
        <Modal
          title="Save reusable preset"
          wide
          onClose={() => {
            setSaving(undefined);
            setRevision((n) => n + 1);
          }}
        >
          <FieldPresetLibrary
            project={sharedOnly ? undefined : project}
            fieldId={sharedOnly ? undefined : field.id}
            kind={saving}
            initialValue={field[saving]}
          />
        </Modal>
      )}
    </>
  );
}
export function FieldPresetLibrary({
  project,
  kind,
  fieldId: initialField,
  initialValue,
}: {
  project?: Project;
  kind: Kind;
  fieldId?: string;
  initialValue?: Settings;
}) {
  const services = useAuthoringServices(),
    [fieldId, setFieldId] = useState(initialField || ""),
    { catalog, error, refresh } = useCatalog(project?.id, fieldId || undefined),
    [editing, setEditing] = useState<FieldPreset>(),
    [name, setName] = useState(""),
    [key, setKey] = useState(""),
    [scope, setScope] = useState<FieldPresetScope["kind"]>(
      project ? "project" : "company",
    ),
    [value, setValue] = useState<Settings>(
      initialValue || (kind === "format" ? formatSettingsSchema.parse({}) : {}),
    ),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false),
    [pending, setPending] = useState(false);
  const context = catalog.context,
    rows = catalog.presets.filter((p) => p.kind === kind),
    canSave =
      !!services.saveFieldPreset &&
      !error &&
      (scope === "site"
        ? context.canManageSite
        : scope === "company"
          ? context.canManageCompany
          : context.canManageProject &&
            !!project &&
            (scope !== "variable" || !!fieldId));
  const save = async (archived = false) => {
    setBusy(true);
    setNotice("");
    try {
      const s =
        editing?.scope ||
        ({
          kind: scope,
          siteId: context.siteId,
          ...(scope !== "site" ? { companyId: context.companyId } : {}),
          ...(["project", "variable"].includes(scope)
            ? { projectId: project!.id }
            : {}),
          ...(scope === "variable" ? { fieldId } : {}),
        } as FieldPresetScope);
      const row = await services.saveFieldPreset!({
        ...(editing ? { id: editing.id } : {}),
        kind,
        key,
        name,
        scope: s,
        value,
        expectedVersion: editing?.version || 0,
        ...(archived ? { archived: true } : {}),
      });
      setEditing(row);
      refresh();
      setNotice(
        archived
          ? "Preset archived. Existing variables keep their pinned settings."
          : `Saved ${row.name} · version ${row.version}`,
      );
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="field-preset-library">
      <p>
        Standard presets are available application wide. Custom presets can
        override the same key for a site, company, project or variable.
        Variables copy the selected version.
      </p>
      {error && <p role="alert">{error}</p>}
      {project && (
        <Control label="Preset variable scope">
          <select
            value={fieldId}
            onChange={(e) => {
              setFieldId(e.target.value);
              setEditing(undefined);
            }}
          >
            <option value="">All project variables</option>
            {project.fields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </Control>
      )}
      <div className="control-grid">
        <section aria-label="Available presets">
          {rows.map((p) => (
            <div key={p.id} className="resource-row">
              <strong>{p.name}</strong>
              <small>
                {" "}
                {p.scope.kind} · v{p.version}
                {p.archived ? " · archived" : ""}
              </small>{" "}
              <Button
                onClick={() => {
                  setEditing(undefined);
                  setName(p.name + " copy");
                  setKey(p.key);
                  setValue(parse(kind, p.value as Settings));
                  setNotice("");
                }}
              >
                Use as starting point
              </Button>
              {p.scope.kind !== "application" && (
                <Button
                  onClick={() => {
                    setEditing(p);
                    setName(p.name);
                    setKey(p.key);
                    setScope(p.scope.kind);
                    setValue(parse(kind, p.value as Settings));
                    setNotice("");
                  }}
                >
                  Edit preset
                </Button>
              )}
            </div>
          ))}
        </section>
        <section aria-label="Preset editor">
          <h3>{editing ? "Edit preset version" : "Create scoped preset"}</h3>
          <Button
            onClick={() => {
              setEditing(undefined);
              setName("");
              setKey("");
              setNotice("");
            }}
          >
            New preset
          </Button>
          <Control label="Preset name">
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </Control>
          <Control
            label="Preset key"
            note="Use the same key as a broader preset to override it in this scope."
          >
            <input
              disabled={!!editing}
              value={key}
              onChange={(e) => setKey(e.target.value)}
            />
          </Control>
          <Control label="Preset scope">
            <select
              disabled={!!editing}
              value={scope}
              onChange={(e) => setScope(e.target.value as typeof scope)}
            >
              <option value="site" disabled={!context.canManageSite}>
                Site · {context.siteName || context.siteId}
              </option>
              <option value="company" disabled={!context.canManageCompany}>
                Company · {context.companyName || context.companyId}
              </option>
              <option
                value="project"
                disabled={!project || !context.canManageProject}
              >
                Project · {project?.name || "open a project"}
              </option>
              <option
                value="variable"
                disabled={!project || !fieldId || !context.canManageProject}
              >
                Variable ·{" "}
                {project?.fields.find((f) => f.id === fieldId)?.label ||
                  "select a variable"}
              </option>
            </select>
          </Control>
          <PolicySettingsEditor
            kind={kind}
            value={value}
            onPendingChange={setPending}
            onApply={(v) => {
              setValue(v);
              setNotice(
                "Settings applied to draft. Save the preset version to store it.",
              );
            }}
          />
          <p>Use Apply settings above before saving the preset.</p>
          <div className="button-row">
            <Button
              disabled={
                busy || pending || !canSave || !name.trim() || !key.trim()
              }
              onClick={() => void save()}
            >
              Save preset version
            </Button>
            {editing && !editing.archived && (
              <Button
                disabled={busy || pending || !canSave}
                onClick={() => void save(true)}
              >
                Archive preset
              </Button>
            )}
          </div>
          {notice && <p role="status">{notice}</p>}
        </section>
      </div>
    </div>
  );
}
